import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate, Outlet } from 'react-router-dom';

import { WorkspaceSkeleton } from '@/app/layouts';
import { RootRouteErrorElement } from '@/app/error';

import { AuthGate } from './AuthGate';
import { PostAuthRedirect } from './PostAuthRedirect';
import { RootLayout } from './RootLayout';
import { autofocusPatientLoader } from './loaders/autofocusPatientLoader';
import { clinicianAreaLoader } from './loaders/clinicianAreaLoader';
import { dashboardLoader } from './loaders/dashboardLoader';
import { patientWorkspaceLoader } from './loaders/patientWorkspaceLoader';
import { rootLayoutLoader } from './loaders/rootLayoutLoader';

import { ClinicianOnboardingGate } from '@/features/clinician-onboarding';
import LoginPage from '@/pages/login/LoginPage';
import DashboardPage from '@/pages/dashboard/DashboardPage';
import AutofocusPatientPage from '@/pages/autofocus-patient/AutofocusPatientPage';
import PatientWorkspacePage from '@/pages/patient-workspace/PatientWorkspacePage';
import { PatientWorkspaceRouteError } from '@/pages/patient-workspace/PatientWorkspaceRouteError';

const NotFoundPage = lazy(() => import('@/pages/not-found/NotFoundPage'));
const KarmaPage = lazy(() => import('@/pages/karma/KarmaPage'));

/**
 * Application route definitions.
 * Routes are code-split via React.lazy for optimal bundle sizes.
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RootRouteErrorElement />,
    loader: rootLayoutLoader,
    hydrateFallbackElement: <WorkspaceSkeleton />,
    children: [
      {
        index: true,
        element: <Navigate to="/login" replace />,
      },
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'clinician',
        element: (
          <AuthGate>
            <ClinicianOnboardingGate>
              <PostAuthRedirect />
            </ClinicianOnboardingGate>
          </AuthGate>
        ),
      },
      // Clinician workspace routes (require onboarding before any loaders run)
      {
        loader: clinicianAreaLoader,
        element: (
          <AuthGate>
            <ClinicianOnboardingGate>
              <Outlet />
            </ClinicianOnboardingGate>
          </AuthGate>
        ),
        children: [
          {
            path: 'dashboard',
            loader: dashboardLoader,
            element: <DashboardPage />,
          },
          {
            path: 'patient',
            loader: autofocusPatientLoader,
            element: <AutofocusPatientPage />,
          },
          {
            path: 'patient/:userId',
            loader: patientWorkspaceLoader,
            errorElement: <PatientWorkspaceRouteError />,
            element: <PatientWorkspacePage />,
          },
          {
            path: 'karma',
            element: <KarmaPage />,
          },
        ],
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
];
